import { Box, Stack, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';

// Created once at module scope: motion owns the `transform`/`opacity` on this
// node, so no conflicting static value may come back into its `sx`.
const MotionDot = motion.create(Box);

const DOT_OPACITY_IDLE = [0.25, 0.45, 0.65] as const;

/**
 * The assistant's "composing" state: three dots breathing in sequence next to
 * the localized status text. The text stays visible (not screen-reader-only)
 * because the label can also carry the specific activity, e.g. "Analisi
 * etichetta…". The whole row is the `role="status"` live region. Under
 * reduced motion the dots hold a graded static opacity — still an indicator,
 * never a blank pause.
 */
export function AssistantTypingIndicator({ label }: { label: string }) {
  const reduceMotion = usePrefersReducedMotion();

  return (
    <Stack direction="row" role="status" spacing={1.25} sx={{ alignItems: 'center' }}>
      <Box aria-hidden="true" sx={{ display: 'flex', gap: '5px', pl: 0.5 }}>
        {[0, 1, 2].map((dotIndex) => (
          <MotionDot
            key={dotIndex}
            animate={
              reduceMotion
                ? { opacity: DOT_OPACITY_IDLE[dotIndex] }
                : {
                    opacity: [0.25, 1, 0.25],
                    transition: {
                      delay: dotIndex * 0.2,
                      duration: 1.2,
                      ease: 'easeInOut',
                      repeat: Infinity,
                    },
                  }
            }
            sx={{ bgcolor: 'primary.main', borderRadius: '50%', height: 6, width: 6 }}
          />
        ))}
      </Box>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
    </Stack>
  );
}
